import { z } from 'zod';

/**
 * O motor de uma sessão: qual CLI está do outro lado.
 *
 * Mora AQUI, e não em `packages/shared`, porque quem precisa dele primeiro é o fio
 * (`SdkIniciarSchema`, `AgenteCotaSchema`), e `shared` já podia depender de
 * `protocolo` — a ordem de construção do monorepo é alfabética e põe `protocolo`
 * antes. Medido em M0-18: a aresta compila, desde que o `tsconfig` do `shared`
 * resolva `@remoto/protocolo` pelo `dist` e não pela fonte.
 *
 * `.default('CLAUDE')` é deliberado: toda carga que não fala de motor É Claude.
 * Sem isso, cada chamador antigo precisaria passar o campo, e o eixo novo viraria
 * uma migração de chamadores em vez de um acréscimo.
 *
 * **O terceiro valor (F16, ADR-0022) é o que tornou o eixo perigoso.** Com dois
 * motores, `motor === 'CODEX' ? a : b` era uma pergunta completa; com três, o
 * `b` passa a receber o OPENCODE calado — e o levantamento de 2026-09-23 achou
 * ~25 ramos assim, um deles auto-aprovando `Read` de outro CLI. Decisão por
 * motor se escreve em `switch` que termina em `motorNaoTratado`, e a guarda
 * `packages/shared/tests/ramos-de-motor.spec.ts` recusa o ramo binário novo.
 */
export const MotorSchema = z.enum(['CLAUDE', 'CODEX', 'OPENCODE']).default('CLAUDE');
export type Motor = z.infer<typeof MotorSchema>;

/** Os três motores, para percorrer sem repetir a lista. */
export const MOTORES = ['CLAUDE', 'CODEX', 'OPENCODE'] as const satisfies readonly Motor[];

/**
 * O nome que aparece na tela. Fica junto do schema para não nascer uma segunda
 * lista noutro pacote — foi assim que `acharMestre` acabou escrito duas vezes.
 */
export const ROTULO_DO_MOTOR: Record<Motor, string> = {
  CLAUDE: 'Claude Code',
  CODEX: 'Codex',
  OPENCODE: 'OpenCode',
};

/**
 * O fim de todo `switch` sobre o motor.
 *
 * Recebe `never`: se um `case` faltar, a chamada NÃO COMPILA — é o compilador,
 * e não a memória de quem escreve, que lembra do terceiro motor. E se um valor
 * que o tipo diz impossível chegar mesmo assim (carga crua, `as` na borda),
 * lança com o nome dele, em vez de cair no ramo de algum outro motor calado.
 *
 * É a forma que substitui o `motor === 'CODEX' ? … : …`: aquele ternário
 * compilava igual com dois ou com dez motores, e mandava todo motor novo para
 * o lado do Claude sem erro nenhum.
 */
export function motorNaoTratado(motor: never): never {
  throw new Error(`motor sem tratamento: ${String(motor)}`);
}

/**
 * Os motores com COTA de assinatura por conta (ADR-0024): o Claude e o Codex
 * contam uma janela semanal por conta, e cada máquina pode estar logada numa.
 * O OpenCode paga por token numa chave da FROTA (D14): não há conta por
 * máquina, nem cota a ratear.
 *
 * Derivado do enum inteiro, e não lista literal: o motor novo tem de passar
 * por `temCotaDeAssinatura` antes de existir aqui.
 */
export const MotorDeCotaSchema = z.enum(MOTORES).exclude(['OPENCODE']);
export type MotorDeCota = z.infer<typeof MotorDeCotaSchema>;

/** Se o motor conta cota de assinatura por conta. `switch`: o quarto motor não compila até alguém decidir. */
export function temCotaDeAssinatura(motor: Motor): motor is MotorDeCota {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return true;
    case 'OPENCODE':
      return false;
    default:
      return motorNaoTratado(motor);
  }
}

/** Os motores de cota, na ordem de `MOTORES`. */
export const MOTORES_DE_COTA: readonly MotorDeCota[] = MOTORES.filter(temCotaDeAssinatura);

/**
 * O sufixo do `uuidTurno` do RESULTADO de uma ferramenta do OpenCode (F16).
 *
 * No OpenCode a chamada e o resultado moram na MESMA parte (`prt_…`, com
 * `state.input` e `state.output`), mas no formato do Claude — que é o que o
 * painel, a busca e o cérebro leem — eles são dois eventos: o `tool_use` do
 * assistente e o `tool_result` do usuário. Sem sufixo, o segundo teria a mesma
 * chave do primeiro, e o gravador o descartaria em SILÊNCIO, furando ainda o
 * `seq` (a mesma lição do `uuidDeSubEvento` abaixo).
 *
 * O esquema inteiro do OpenCode (Interface congelada, item 11): mensagem do
 * usuário = `msg_…` nu; parte de texto, raciocínio ou `tool_use` = `prt_…` nu;
 * resultado de ferramenta = `prt_…` + este sufixo; uso = `<msg assistente>` +
 * `SUFIXO_USO_DO_MOTOR` (`:u`, em `@remoto/shared`); erro = `<msg usuário>:e`.
 *
 * Mora aqui, e não no daemon, pela razão que o `'e'` do `uuidDeSubEvento`
 * mora: o esquema de sufixos com dois donos é como uma colisão nasce sem
 * ninguém ver. O spec prende que `:t` não colide com `:r`, `:c<n>`, `:e` nem
 * `:u`. Os ids do OpenCode não conterem `:` é medição da Fase 0, não suposição
 * escrita aqui.
 */
export const SUFIXO_RESULTADO_DE_FERRAMENTA = ':t';

/**
 * Gera o `uuidTurno` de um evento sintetizado a partir de um item do Codex.
 *
 * Um `ThreadItem` vira N eventos: a fala, o raciocínio, cada pedaço de saída de
 * comando. A chave única é `(sessaoId, uuidTurno)`, e o gravador **descarta
 * duplicata em silêncio** — sem sufixo, o segundo evento do mesmo item não
 * existiria, e ainda deixaria um buraco no `seq`, porque `ultimo_seq` já foi
 * somado pelo tamanho do lote.
 *
 * O `item.id` do Codex é opaco e globalmente único (medido em M0-11: UUIDv7 no
 * `userMessage`, `msg_…` no `agentMessage`), NÃO um ordinal que reinicia no
 * resume — por isso ele basta como raiz, sem prefixo de turno.
 *
 * O papel `'e'` é o **erro do motor** no fim do turno, e a raiz dele é o
 * `turn.id`, não um item: o erro não vem de item nenhum. Ele mora aqui, e não
 * num `+ ':e'` escrito no host, porque o esquema de sufixos ter dois donos é
 * exatamente como uma colisão nasce sem ninguém ver.
 *
 * **O que este esquema NÃO garante, dito antes que alguém descubra caro.** O
 * evento principal usa o id NU, e os outros usam o id mais sufixo: então um id
 * que já terminasse em `:r` ou `:c<n>` colidiria com o sub-evento de outro item.
 * Não é hipótese remota nem defeito corrigido aqui — é o preço da forma
 * `<id>`, `<id>:r`, `<id>:c<n>`, que é decisão do plano e não se reabre sozinha.
 *
 * Hoje não se viu acontecer — e o que se mediu é pouco: M0-11 viu os ids de
 * `userMessage` (UUIDv7) e de `agentMessage` (`msg_…`), e nenhum contém `:`. Os
 * ids de comando, de edição e de chamada MCP NÃO foram medidos (revisão do PR 58,
 * 2.2.15 — esta linha dizia "inalcançável, por medição"). Se um contiver, o
 * sintoma é o pior possível —
 * o gravador descarta a duplicata CALADO e ainda fura o `seq`. O caso que
 * nomeia essa colisão está no spec, para que trocar o separador não seja um
 * gesto silencioso.
 */
export function uuidDeSubEvento(itemId: string, papel?: 'r' | 'c' | 'e', indice?: number): string {
  if (papel === undefined) return itemId;
  if (papel === 'r') return itemId + ':r';
  if (papel === 'e') return itemId + ':e';
  return itemId + ':c' + String(indice ?? 0);
}
